/**
 * Hook V2 — useApprenantPedagogie
 *
 * Phase wiring 3 : branche sur `GET /api/v1/v2/apprenants/:id/pedagogie`.
 * Source : Zoho heures (sync/async/réalisées) + approximation Moodle.
 * Fallback mock si API indisponible.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export interface SeanceItem {
  date: string;
  type: 'visio' | 'présentiel' | 'e-learning';
  duration: number;
  topic: string;
  emargementSigne: boolean;
}

export interface DevoirItem {
  title: string;
  dueDate: string;
  submittedAt: string | null;
  score: number | null;
}

export interface ApprenantPedagogie {
  apprenantId: string;
  heuresSyncDone: number;
  heuresSyncTotal: number;
  heuresAsyncDone: number;
  heuresAsyncTotal: number;
  moodleConnectionHours: number;
  moodleProgressPct: number;
  derniereConnexion: string | null;
  prochaineEcheance: string | null;
  seances: SeanceItem[];
  devoirs: DevoirItem[];
}

const MARTINE_MOCK: ApprenantPedagogie = {
  apprenantId: 'demo-martine',
  heuresSyncDone: 18,
  heuresSyncTotal: 30,
  heuresAsyncDone: 36,
  heuresAsyncTotal: 60,
  moodleConnectionHours: 42.5,
  moodleProgressPct: 58,
  derniereConnexion: '2026-05-26T20:15:00Z',
  prochaineEcheance: '2026-05-30T14:00:00Z',
  seances: [],
  devoirs: [],
};

async function fetchPedagogie(
  id: string,
): Promise<ApprenantPedagogie & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantPedagogie>(`/api/v1/v2/apprenants/${id}/pedagogie`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantPedagogie(id: string): {
  data: (ApprenantPedagogie & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-pedagogie', id],
    queryFn: () => fetchPedagogie(id),
    staleTime: 60_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
